import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useLiveQuery } from 'dexie-react-hooks';
import { useCodexStore } from './codexStore';
import { listEngineRuns } from '@/db/engineRuns';
import { ENTITY_FIELDS } from '@/lib/schemas/entityFields';
import { deriveCoherenceGaps, type GapSignal } from './derive/coherence';
import { deriveGraph, type DerivedGraph } from './derive/graph';
import { deriveAxisModel, deriveMirrorPairs, type AxisModel, type MirrorPair } from './derive/thesis';
import { deriveBeatContext, type BeatContext } from './derive/beatContext';
import { deriveEnginePressure, type EnginePressure } from '@/engine/canonBundle';
import type { Entity, EntityType, Link } from '@/types/entity';

const active = (e: Entity) => !e.deletedAt;

export const useProject = () => useCodexStore((s) => s.project);
export const useSaveState = () => useCodexStore((s) => s.saveState);
export const useHydrated = () => useCodexStore((s) => s.hydrated);

export const useEntity = (id?: string) => useCodexStore((s) => (id ? s.entities[id] : undefined));
export const useLinkById = (id?: string) => useCodexStore((s) => (id ? s.links[id] : undefined));

/** 전체 entities Record (안정 참조 — 컴포넌트 내 lookup용) */
export const useEntityMap = () => useCodexStore((s) => s.entities);

// 아래 selector들은 *스토어 원본 객체의 배열*만 반환한다.
// useShallow는 요소를 참조로 비교하므로, 새 래퍼 객체를 만들면 캐시가 깨져
// useSyncExternalStore 무한 루프(React #185)가 난다 → 반드시 원본 ref만 담을 것.

export const useActiveEntities = () =>
  useCodexStore(useShallow((s) => Object.values(s.entities).filter(active)));

export const useEntitiesByType = (type: EntityType) =>
  useCodexStore(
    useShallow((s) => Object.values(s.entities).filter((e) => active(e) && e.type === type)),
  );

export const useDeletedEntities = () =>
  useCodexStore(useShallow((s) => Object.values(s.entities).filter((e) => !!e.deletedAt)));

export const useActiveLinks = () =>
  useCodexStore(useShallow((s) => Object.values(s.links).filter((l) => !l.deletedAt)));

/** 선택 엔티티에 닿는 활성 링크 (원본 Link ref만 → 안정) */
export const useNeighborLinks = (id?: string) =>
  useCodexStore(
    useShallow((s) =>
      id
        ? Object.values(s.links).filter(
            (l) => !l.deletedAt && (l.from === id || l.to === id),
          )
        : [],
    ),
  );

/** 역참조 — @멘션(refs 캐시) + entityref/multiselect 필드가 이 엔티티를 가리키는 모든 엔티티 */
export function useRefsOf(id?: string): Entity[] {
  const entities = useEntityMap();
  return useMemo(() => {
    if (!id) return [];
    const out: Entity[] = [];
    for (const e of Object.values(entities)) {
      if (!active(e) || e.id === id) continue;
      if (e.refs?.includes(id)) {
        out.push(e);
        continue;
      }
      const defs = [...(ENTITY_FIELDS[e.type] ?? []), ...(e.customFields ?? [])];
      const hit = defs.some(
        (d) =>
          (d.type === 'entityref' && e.fields[d.key] === id) ||
          (d.type === 'multiselect' &&
            Array.isArray(e.fields[d.key]) &&
            (e.fields[d.key] as unknown[]).includes(id)),
      );
      if (hit) out.push(e);
    }
    return out;
  }, [id, entities]);
}

/** 활성 beat 엔티티 (플롯 보드가 컴포넌트에서 셀별로 그룹) */
export const useBeats = () =>
  useCodexStore(useShallow((s) => Object.values(s.entities).filter((e) => active(e) && e.type === 'beat')));

export interface CodexStats {
  total: number;
  byType: Record<string, number>;
  openQuestions: number;
  totalQuestions: number;
}

/** 안정 소스(useActiveEntities)에서 useMemo로 파생 → 객체 ref 안정 */
export function useStats(): CodexStats {
  const ents = useActiveEntities();
  return useMemo(() => {
    const byType: Record<string, number> = {};
    let totalQuestions = 0;
    let resolved = 0;
    for (const e of ents) {
      byType[e.type] = (byType[e.type] ?? 0) + 1;
      if (e.type === 'question') {
        totalQuestions++;
        if (e.fields.status === 'resolved') resolved++;
      }
    }
    return { total: ents.length, byType, openQuestions: totalQuestions - resolved, totalQuestions };
  }, [ents]);
}

/**
 * 파생 coherence gap — "다음에 채워야 할 것"을 점수순으로.
 * 안정 소스(useActiveEntities/Links)에서 useMemo 파생 → 객체 ref 안정(#185 안전).
 */
export function useCoherenceGaps(): GapSignal[] {
  const ents = useActiveEntities();
  const links = useActiveLinks();
  return useMemo(() => deriveCoherenceGaps(ents, links), [ents, links]);
}

/**
 * 공유 파생 그래프 — degree·incoming 멘션·공동등장(파생 관계).
 * 안정 소스에서 useMemo 파생(#185 안전). 도감의 "함께 등장" 등이 소비.
 */
export function useDerivedGraph(): DerivedGraph {
  const ents = useActiveEntities();
  const links = useActiveLinks();
  return useMemo(() => deriveGraph(ents, links), [ents, links]);
}

/** 명제의 모양 — 행성 axis 분포·무게중심·쏠림(분석↔합일). 정적 산문이 아닌 살아있는 집계. */
export function useAxisModel(): AxisModel {
  const planets = useEntitiesByType('planet');
  return useMemo(() => deriveAxisModel(planets), [planets]);
}

/** 거울 대립쌍(명제 척추) — mirror 링크에서 파생. */
export function useMirrorPairs(): MirrorPair[] {
  const links = useActiveLinks();
  const emap = useEntityMap();
  return useMemo(() => deriveMirrorPairs(links, emap), [links, emap]);
}

/**
 * 비트 맥락 번들 — 선택 비트가 묶은 캐릭터 OS·세계규칙·플롯·테마를 파생(레이어 A).
 * 안정 소스(useEntity/useEntityMap/useActiveLinks/useDerivedGraph/useProject) 위 단일 useMemo(#185 안전).
 * beat 타입이 아니면 null(note 선택 시 맥락/보조 비활성).
 */
export function useBeatContext(beatId?: string): BeatContext | null {
  const beat = useEntity(beatId);
  const emap = useEntityMap();
  const links = useActiveLinks();
  const graph = useDerivedGraph();
  const project = useProject();
  return useMemo(() => {
    if (!beat || beat.type !== 'beat') return null;
    return deriveBeatContext(beat, emap, links, graph, { colOrder: project?.plotColumns?.map((c) => c.id) });
  }, [beat, emap, links, graph, project]);
}

/**
 * Living Canon Engine 압력 신호 — assertion metadata + 저장된 최신 engine run(Dexie engineRuns) 합성.
 * report는 **최신 1건만** 넘긴다: run은 전체 번들 스냅샷이라 과거 run까지 합산하면
 * 이미 해소된 위반·패치가 이중 계상된다. useLiveQuery라 saveEngineRun 직후 배지가 산다.
 */
export function useEnginePressure(): EnginePressure {
  const ents = useActiveEntities();
  const project = useProject();
  const latestRun = useLiveQuery(
    () => listEngineRuns(project?.id).then((runs) => runs[0] ?? null),
    [project?.id],
    null,
  );
  return useMemo(
    () => deriveEnginePressure(ents, latestRun ? [latestRun.report] : []),
    [ents, latestRun],
  );
}

/** 컴포넌트에서 링크+상대 엔티티를 합성 (안정 소스 기반) */
export function useNeighbors(id?: string): { link: Link; entity: Entity }[] {
  const links = useNeighborLinks(id);
  const entities = useEntityMap();
  return useMemo(() => {
    if (!id) return [];
    const out: { link: Link; entity: Entity }[] = [];
    for (const l of links) {
      const otherId = l.from === id ? l.to : l.from;
      const e = entities[otherId];
      if (e && active(e)) out.push({ link: l, entity: e });
    }
    return out;
  }, [id, links, entities]);
}
